// glob-pattern-intersection.ts
//
// Security re-audit M-R3: can two allowed_paths globs match the same path?
// Decided exactly — no sample paths — for the verifier's glob language
// (result-verifier-scope-checks.ts `matchesGlob`): `**` matches any run of
// characters, `*` any run without `/`, every other character itself. `?` is
// NOT supported here (the verifier leaves it a regex quantifier), so callers
// refuse patterns holding it before asking.
//
// Product construction: a state is a position in each pattern; a wildcard
// may be left (it matched nothing more) or may consume a character the other
// side's literal emits. The patterns intersect iff both ends are reachable.

type GlobToken = { kind: "any" } | { kind: "segment" } | { kind: "char"; char: string };

function tokenize(pattern: string): GlobToken[] {
  const tokens: GlobToken[] = [];
  for (let i = 0; i < pattern.length; i++) {
    if (pattern.startsWith("**", i)) {
      tokens.push({ kind: "any" });
      i++;
    } else if (pattern[i] === "*") {
      tokens.push({ kind: "segment" });
    } else {
      tokens.push({ kind: "char", char: pattern[i] ?? "" });
    }
  }
  return tokens;
}

const accepts = (wildcard: GlobToken, char: string) =>
  wildcard.kind === "any" || (wildcard.kind === "segment" && char !== "/");

/** True iff some path matches both `a` and `b` (whole-path glob match). */
export function globsIntersect(a: string, b: string): boolean {
  const left = tokenize(a);
  const right = tokenize(b);
  const seen = new Set<string>();
  const queue: [number, number][] = [[0, 0]];
  while (queue.length > 0) {
    const [i, j] = queue.pop() ?? [0, 0];
    const key = `${i},${j}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (i === left.length && j === right.length) return true;
    const l = left[i];
    const r = right[j];
    if (l !== undefined && l.kind !== "char") queue.push([i + 1, j]);
    if (r !== undefined && r.kind !== "char") queue.push([i, j + 1]);
    if (l?.kind === "char" && r?.kind === "char" && l.char === r.char) queue.push([i + 1, j + 1]);
    if (l?.kind === "char" && r !== undefined && accepts(r, l.char)) queue.push([i + 1, j]);
    if (r?.kind === "char" && l !== undefined && accepts(l, r.char)) queue.push([i, j + 1]);
  }
  return false;
}
